// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Where to land after signing in.
 *
 * The URL is the one source of truth: `/login?next=/site/GA-10/power-flow`.
 * `ProtectedRoute` writes it, `Login` reads it, and it rides through the CIAM
 * round-trip as MSAL's `redirectStartPage`, so a refresh of `/login` or a
 * return from CIAM both land on the page the user asked for.
 *
 * The one hop a URL can't carry is a sign-*out*: `logoutRedirect` returns to
 * a fixed, registered `postLogoutRedirectUri`. The session-expiry flows stash
 * the page in `sessionStorage` before logging out, and `Login` folds that
 * stash straight into `?next=` on arrival — so it never outlives one hop.
 */
export const NEXT_PARAM = 'next';
const RETURN_PATH_KEY = 'helios_return_path';

/**
 * A same-origin, in-app path, or null. Guards `?next=` against being used as
 * an open redirect (`//evil.example`, `/\evil.example`, absolute URLs) and
 * against pointing back at `/login` itself.
 */
export function safeReturnPath(raw: string | null | undefined): string | null {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) return null;
  if (raw === '/' || raw === '/login' || raw.startsWith('/login?') || raw.startsWith('/login#'))
    return null;
  return raw;
}

/** `/login`, carrying the full current location (path, query and hash) as `?next=`. */
export function loginPathFor({
  pathname,
  search,
  hash,
}: {
  pathname: string;
  search: string;
  hash: string;
}): string {
  const next = safeReturnPath(`${pathname}${search}${hash}`);
  return next ? `/login?${NEXT_PARAM}=${encodeURIComponent(next)}` : '/login';
}

export function stashReturnPath(path: string): void {
  sessionStorage.setItem(RETURN_PATH_KEY, path);
}

/** Reads and clears the stashed path in one call — it's a one-time consume. */
export function consumeReturnPath(): string | null {
  const path = sessionStorage.getItem(RETURN_PATH_KEY);
  if (path) sessionStorage.removeItem(RETURN_PATH_KEY);
  return path;
}
